<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link 
              to="/" 
              class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
              首页
            </router-link>
            <div class="flex items-center gap-2">
              <span class="text-2xl">🦁</span>
              <span class="font-semibold text-psychology-primary">动物性格测试</span>
            </div>
          </div>
          <button 
            v-if="currentQuestion > 1"
            @click="previousQuestion" 
            class="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            上一题
          </button>
        </div>

        <!-- 进度条 -->
        <div class="mt-4">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm text-gray-600">答题进度</span>
            <span class="text-sm font-medium text-psychology-primary">{{ currentQuestion }}/60</span>
          </div>
          <div class="relative w-full overflow-hidden rounded-full bg-gray-200 h-2">
            <div 
              class="h-full bg-psychology-primary transition-all"
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="container mx-auto px-4 py-8">
      <div class="max-w-3xl mx-auto">
        <!-- 题目卡片 -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <!-- 题目头部 -->
          <div class="bg-gradient-to-r from-psychology-primary to-psychology-secondary px-6 py-4">
            <div class="flex items-center justify-between text-white">
              <span class="text-lg font-semibold">第 {{ currentQuestion }} 题</span>
              <span class="text-sm opacity-90">还剩 {{ remainingQuestions }} 题</span>
            </div>
          </div>

          <!-- 题目内容 -->
          <div class="p-6 sm:p-8">
            <!-- 题目文字 -->
            <h2 class="text-xl sm:text-2xl font-medium text-gray-800 mb-8 leading-relaxed">
              {{ currentQuestionData.question }}
            </h2>

            <!-- 选项列表 -->
            <div class="space-y-3">
              <button
                v-for="(text, key) in currentQuestionData.options"
                :key="key"
                @click="selectOption(key)"
                :disabled="isOptionDisabled"
                :class="[
                  'w-full text-left p-4 rounded-xl border-2 transition-all duration-200',
                  selectedAnswer === key
                    ? 'border-psychology-primary bg-psychology-primary-10 shadow-md'
                    : 'border-gray-200 bg-white hover:shadow-sm',
                  isOptionDisabled ? 'cursor-not-allowed opacity-60' : ''
                ]"
              >
                <div class="flex items-start gap-3">
                  <!-- 选项标签 -->
                  <div 
                    :class="[
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold',
                      selectedAnswer === key 
                        ? 'bg-psychology-primary text-white' 
                        : 'bg-gray-100 text-gray-600'
                    ]"
                  >
                    {{ key }}
                  </div>
                  <!-- 选项文本 -->
                  <div class="flex-1 pt-1">
                    <p class="text-gray-700 leading-relaxed">{{ text }}</p>
                  </div>
                  <!-- 选中标记 -->
                  <div 
                    v-if="selectedAnswer === key"
                    class="flex-shrink-0 text-psychology-primary"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- 底部操作栏 -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <div class="flex items-center justify-between">
              <button 
                v-if="currentQuestion > 1"
                @click="previousQuestion"
                class="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors border border-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                上一题
              </button>
              <div v-else></div>

              <button 
                v-if="currentQuestion < 60"
                @click="nextQuestion"
                :disabled="!selectedAnswer"
                :class="[
                  'inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all',
                  selectedAnswer
                    ? 'bg-gradient-to-r from-psychology-primary to-psychology-secondary text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                下一题
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>
              <button 
                v-else
                @click="submitAnswers"
                :disabled="!selectedAnswer"
                :class="[
                  'inline-flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all',
                  selectedAnswer
                    ? 'bg-gradient-to-r from-psychology-primary to-psychology-success text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                查看结果
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 提示信息 -->
        <div class="mt-6 text-center">
          <p class="text-sm text-gray-500">💡 凭第一感觉选择，不要过度思考哦</p>
        </div>
      </div>
    </main>

    <!-- 回到顶部 -->
    <BackToTop />

    <!-- 测试工具（仅开发环境） -->
    <TestQuickFill
      :answers="answers"
      :current-question="currentQuestion"
      @update-answers="handleUpdateAnswers"
      @submit="submitAnswers"
    />

    <!-- 确认离开对话框 -->
    <ConfirmDialog
      v-if="showExitConfirm"
      title="确认离开？"
      message="你的答题进度将被保存，下次可以继续答题。"
      confirm-text="离开"
      cancel-text="继续答题"
      @confirm="confirmExit"
      @cancel="showExitConfirm = false"
    />
  </div>
</template>

<script>
import { animalQuestions } from '@/data/animalQuestions'
import BackToTop from '@/components/BackToTop.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import TestQuickFill from '@/components/TestQuickFill.vue'
import { 
  saveSessionProgress, 
  restoreSessionProgress,
  getSessionById,
  createSession
} from '@/utils/sessionStorage'
import { useToast } from 'vue-toastification'

export default {
  name: 'QuestionnairePage',
  components: {
    BackToTop,
    ConfirmDialog,
    TestQuickFill
  },
  data() {
    return {
      currentQuestion: 1,
      answers: {},
      sessionId: null,
      showExitConfirm: false,
      isSaving: false,
      isOptionDisabled: false  // 控制选项按钮是否禁用
    }
  },
  computed: {
    currentQuestionData() {
      return animalQuestions[this.currentQuestion - 1] || {}
    },
    selectedAnswer() {
      console.log("selectedAnswer",this.answers[this.currentQuestion]);
      return this.answers[this.currentQuestion]
    },
    progressPercent() {
      return Math.round((this.currentQuestion / 60) * 100)
    },
    remainingQuestions() {
      return 60 - this.currentQuestion
    },
    answeredCount() {
      return Object.keys(this.answers).length
    }
  },
  mounted() {
    // 恢复或创建会话
    this.initSession()
    
    // 自动保存
    this.startAutoSave()
  },
  beforeUnmount() {
    // 清除自动保存定时器
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer)
    }
  },
  methods: {
    initSession() {
      const query = this.$route.query
      
      if (query.sessionId) {
        // 恢复已有会话
        const session = getSessionById(query.sessionId)
        if (session) {
          this.sessionId = session.id
          this.answers = session.responses || {}
          this.currentQuestion = session.currentQuestion || 1
          return
        }
      }
      
      // 尝试恢复进度
      const restored = restoreSessionProgress()
      if (restored && restored.session) {
        this.sessionId = restored.session.id
        this.answers = restored.progress.responses || {}
        this.currentQuestion = restored.session.currentQuestion || 1
        return
      }
      
      // 创建新会话
      const newSession = createSession({
        demographics: query.demographics ? JSON.parse(query.demographics) : {}
      })
      this.sessionId = newSession.id
      
      // 更新路由（不刷新页面）
      if (this.$route.query.sessionId !== this.sessionId) {
        this.$router.replace({
          name: 'Questionnaire',
          query: { sessionId: this.sessionId }
        })
      }
    },
    
    selectOption(option) {
      // 防止重复点击
      if (this.isOptionDisabled) return

      // 立即禁用所有选项
      this.isOptionDisabled = true

      // 记录点击时的题号（用于双重保险）
      const questionNumberWhenClicked = this.currentQuestion

      // 保存答案
      this.answers[this.currentQuestion] = option

      // 立即保存进度
      this.saveProgress()

      // 短暂延迟后自动跳转到下一题并重新启用选项
      setTimeout(() => {
        // 双重检查：确保还在同一题目
        if (this.currentQuestion === questionNumberWhenClicked && this.currentQuestion < 60) {
          this.nextQuestion()
        }

        // 重新启用选项
        this.isOptionDisabled = false
      }, 200)
    },
    
    nextQuestion() {
      if (this.currentQuestion < 60) {
        console.log("nextQuestion1", this.currentQuestion);
        this.currentQuestion++
        console.log("nextQuestion2", this.currentQuestion);
        this.saveProgress()
        this.scrollToTop()
      }

      // 确保选项被启用
      this.isOptionDisabled = false
    },
    
    previousQuestion() {
      if (this.currentQuestion > 1) {
        this.currentQuestion--
        this.scrollToTop()
      }

      // 确保选项被启用
      this.isOptionDisabled = false
    },
    
    submitAnswers() {
      // 检查是否全部回答
      if (this.answeredCount < 60) {
        const unanswered = []
        for (let i = 1; i <= 60; i++) {
          if (!this.answers[i]) {
            unanswered.push(i)
          }
        }
        
        const toast = useToast()
        toast.warning(`还有 ${unanswered.length} 道题未作答，请完成所有题目后再提交。`)
        
        // 跳转到第一个未回答的题目
        this.currentQuestion = unanswered[0]
        this.scrollToTop()
        return
      }
      
      // 跳转到计算页面
      this.$router.push({
        name: 'Calculating',
        query: { sessionId: this.sessionId }
      })
    },
    
    saveProgress() {
      if (!this.sessionId || this.isSaving) return
      
      this.isSaving = true
      saveSessionProgress(this.sessionId, {
        responses: this.answers,
        currentQuestion: this.currentQuestion
      })
      this.isSaving = false
    },
    
    startAutoSave() {
      // 每30秒自动保存一次
      this.autoSaveTimer = setInterval(() => {
        this.saveProgress()
      }, 30000)
    },
    
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    
    goBack() {
      if (this.answeredCount > 0) {
        this.showExitConfirm = true
      } else {
        this.$router.back()
      }
    },
    
    confirmExit() {
      this.saveProgress()
      this.$router.back()
    },
    
    /**
     * 处理测试工具更新答案
     * @param {Object} newAnswers - 新的答案对象
     */
    handleUpdateAnswers(newAnswers) {
      this.answers = { ...newAnswers }
      // 保存进度
      this.saveProgress()
      // 跳转到第一题
      this.currentQuestion = 1
      this.scrollToTop()
    }
  }
}
</script>

<style scoped>
/* 按钮基础样式 */
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* 选项卡片动画 */
button[class*="border-"] {
  transform: translateY(0);
  transition: all 0.2s ease;
}

button[class*="border-"]:hover:not(:disabled) {
  transform: translateY(-2px);
}

button[class*="border-"]:active:not(:disabled) {
  transform: translateY(0);
}

/* 进度条动画 */
@keyframes slideIn {
  from {
    width: 0;
  }
}

/* 响应式调整 */
@media (max-width: 640px) {
  h2 {
    font-size: 1.125rem;
  }
}
</style>
